import React, { useState } from 'react';
import { InitialInteractionActionModal } from '@fiverr-private/legal_documents_package';
import CustomModal from '../components/ComplianceModal/CustomModal';
import { ComplianceType, ComplianceTypes } from '../components/ComplianceModal/types';
import { SuccessStepModalContent } from '../components/SuccessStepModalContent/SuccessStepModalContent';
import { useSellerConfigData } from '../api/useSellerConfigData';
import { useOrganizationPackagesData } from '../api/useOrganizationPackagesData';
import { usePayment } from './usePayment';
import useRedirectFromUpgradePayment from './useRedirectFromUpgradePayment';

export interface OpenComplianceModal {
    sellerId: number;
    sellerName: string;
    fromComplianceType?: ComplianceType;
}

export type ModalTypes = 'success_modal' | ComplianceType | '';

export interface UseComplianceActionModalOptions {
    pageName?: string;
    sellerId?: number;
}

export const useComplianceActionModal = ({ pageName = '', sellerId: _sellerId }: UseComplianceActionModalOptions) => {
    const [isOpen, setIsOpen] = useState(false);
    // default value for translation
    const [openModalType, setOpenModalType] = useState<ModalTypes>(ComplianceTypes.BACKGROUND_CHECK);
    const [successSourceComplianceType, setSuccessSourceComplianceType] = useState<ComplianceType | undefined>();
    const [sellerId, setSellerId] = useState<number | undefined>(_sellerId);
    const [sellerName, setSellerName] = useState<string>('');
    const { handlePayment } = usePayment();
    const { sellerEntityType } = useSellerConfigData(sellerId);
    const { upgradeTriggered } = useRedirectFromUpgradePayment({
        onSourceChange: setOpenModalType,
        onSellerNameChange: setSellerName,
        onQueryTriggered: () => setIsOpen(true),
        onSellerIdChange: setSellerId,
    });

    const { isFree, legalDocumentsPackageData } = useSellerConfigData(sellerId);
    const { canUpgradeToPro } = useOrganizationPackagesData();

    const openModal =
        (complianceType: ModalTypes) =>
        ({ sellerId, sellerName, fromComplianceType }: OpenComplianceModal) => {
            setIsOpen(true);
            setOpenModalType(complianceType);
            setSellerId(sellerId);
            setSellerName(sellerName);
            if (complianceType === 'success_modal' && fromComplianceType) {
                setSuccessSourceComplianceType(fromComplianceType);
            }
        };

    const openBackgroundCheckModal = openModal(ComplianceTypes.BACKGROUND_CHECK);
    const openSendLegalDocsModal = openModal(ComplianceTypes.LEGAL_DOCS);
    const openWFCModal = openModal(ComplianceTypes.WORKFORCE_CLASSIFICATION);
    const openSuccessStep = openModal('success_modal');

    const closeModal = () => {
        setIsOpen(false);
        setTimeout(resetState, 400);
    };

    const resetState = () => {
        setOpenModalType('');
        setSellerId(undefined);
    };

    const isCustomModalOpen = [ComplianceTypes.WORKFORCE_CLASSIFICATION, ComplianceTypes.BACKGROUND_CHECK].includes(
        openModalType as ComplianceType
    );

    return {
        openBackgroundCheckModal,
        openSendLegalDocsModal,
        openWFCModal,
        openSuccessStep,
        closeModal,
        ModalContent: (
            <>
                {openModalType && (
                    <CustomModal
                        isOpen={isCustomModalOpen && isOpen}
                        onClose={closeModal}
                        complianceType={openModalType as ComplianceType}
                        requeseteeId={sellerId as number}
                        requeseteeUserName={sellerName}
                    />
                )}
                {successSourceComplianceType && (
                    <SuccessStepModalContent
                        sellerName={sellerName}
                        sellerId={sellerId as number}
                        onClose={closeModal}
                        isOpen={openModalType === 'success_modal' && isOpen}
                        fromComplianceType={successSourceComplianceType as ComplianceType}
                        canUpgradeToPro={canUpgradeToPro}
                    />
                )}
                <InitialInteractionActionModal
                    sellerName={sellerName}
                    sellerId={sellerId as number}
                    handlePayment={(templateIds: string[]) =>
                        handlePayment({
                            complianceType: ComplianceTypes.LEGAL_DOCS,
                            sellerId: sellerId || 0,
                            templateIds,
                            isFree,
                            discountInPercents: legalDocumentsPackageData?.discount,
                            requeseteeUserName: sellerName,
                        })
                    }
                    isOpen={openModalType === ComplianceTypes.LEGAL_DOCS && isOpen}
                    biData={{ pageName }}
                    closeModal={closeModal}
                    sellerEntityType={sellerEntityType}
                    wasRedirectedFromUpgrade={upgradeTriggered}
                />
            </>
        ),
    };
};
